import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Checkbox, Input, Menu, Modal, Popconfirm, Select, Switch, Tag, Typography, message } from "antd";
import {
  CheckSquareOutlined,
  AppstoreOutlined,
  ExperimentOutlined,
  CopyOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { getAllWidgetDefs, getWidgetDef } from "../widgetRegistry";
import { useDashboard } from "../Contexts/dashboardHooks";
import { serializeDashboardSpec } from "../Contexts/serializeSpec";
import { useTemplateExportMaps } from "../Templates/templateContext";
import { tokenizeDashboard } from "../Templates/tokenizeDashboard";
import { captureFirstPagePng, downloadTemplateFiles } from "../Templates/exportTemplate";
import { computeContractHash } from "../widgetContract";
import { CHECKLIST_ITEMS, type ContractStatusKind } from "../widgetContractCheck";
import {
  EXPERIMENTAL_FLAG_META,
  getExperimentalFlags,
  setExperimentalFlags,
  resetExperimentalFeatures,
  type ExperimentalFlags,
} from "./experimentalFlags";
import type { GridWidgetType } from "../types";

const { Text, Paragraph, Title } = Typography;

type Section = "checklist" | "templates" | "flags";

const STATUS_COLOR: Record<ContractStatusKind, string> = {
  wired: "green",
  optout: "geekblue",
  missing: "red",
  pending: "default",
};

/**
 * Admin-only panel (see Docs/WIDGET_CONTRACT_DESIGN.md), reachable from the Debug menu.
 * A sidebar of tools; the Component Checklist is the first. Each concern of the widget
 * contract is explained inline, its live wiring is shown (wired / NoX opt-out / missing),
 * and attesting the required ones reveals the deterministic contract hash to paste back
 * into the definition — so a schema change forces a documented re-review.
 */
export function AdminPanelModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [section, setSection] = useState<Section>("checklist");
  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Widget Admin"
      footer={null}
      width={880}
      styles={{ body: { padding: 0 } }}
    >
      <div style={{ display: "flex", minHeight: 540 }}>
        <div style={{ width: 214, borderRight: "1px solid #f0f0f0", paddingTop: 8, background: "#fafafa" }}>
          <Menu
            mode="inline"
            selectedKeys={[section]}
            onClick={(e) => setSection(e.key as Section)}
            style={{ border: "none", background: "transparent" }}
            items={[
              { key: "checklist", icon: <CheckSquareOutlined />, label: "Component Checklist" },
              { key: "templates", icon: <AppstoreOutlined />, label: "Template Manager" },
              { key: "flags", icon: <ExperimentOutlined />, label: "Experimental Flags" },
            ]}
          />
        </div>
        <div style={{ flex: 1, padding: "20px 24px", maxHeight: 640, overflowY: "auto" }}>
          {section === "checklist" && <ChecklistSection />}
          {section === "templates" && <TemplateManagerSection />}
          {section === "flags" && <ExperimentalFlagsSection />}
        </div>
      </div>
    </Modal>
  );
}

function ChecklistSection() {
  const defs = getAllWidgetDefs();
  const [type, setType] = useState<GridWidgetType>(defs[0]?.type);
  const [checked, setChecked] = useState<Record<string, boolean>>({});

  // Reset attestations whenever the selected widget changes.
  useEffect(() => setChecked({}), [type]);

  const def = getWidgetDef(type);
  const requiredItems = useMemo(() => CHECKLIST_ITEMS.filter((i) => i.required), []);
  const allRequiredChecked = !!def && requiredItems.every((i) => checked[i.key]);

  const expectedHash = def ? computeContractHash(def) : "";
  const storedHash = def?.contractHash;
  const isStale = storedHash != null && storedHash !== expectedHash;

  return (
    <>
      <Title level={5} style={{ marginTop: 0, marginBottom: 4 }}>
        Component Checklist
      </Title>
      <Paragraph type="secondary" style={{ marginBottom: 16, fontSize: 13 }}>
        Every per-widget concern in the widget contract. Review each below — its live state is
        shown on the right — then attest the required ones to reveal the contract hash to paste into
        the definition. Changing a widget's schema changes the hash and forces a re-review.
      </Paragraph>

      <Select
        value={type}
        onChange={setType}
        showSearch
        options={defs.map((d) => ({ label: d.type, value: d.type }))}
        style={{ width: "100%", marginBottom: 16 }}
      />

      {def && (
        <>
          {isStale && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="Stale contract hash"
              description="This widget's schema changed since it was last reviewed. Re-attest every item and paste the new hash."
            />
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {CHECKLIST_ITEMS.map((item) => {
              const complete = item.complete(def);
              const status = item.status(def);
              return (
                <div
                  key={item.key}
                  style={{
                    display: "flex",
                    gap: 12,
                    padding: "12px 14px",
                    border: "1px solid #f0f0f0",
                    borderRadius: 8,
                    background: item.required ? "#fff" : "#fafafa",
                  }}
                >
                  <Checkbox
                    disabled={!complete}
                    checked={!!checked[item.key]}
                    onChange={(e) => setChecked((c) => ({ ...c, [item.key]: e.target.checked }))}
                    style={{ marginTop: 2 }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                      <Text strong>
                        {item.label}
                        {!item.required && (
                          <Text type="secondary" style={{ fontWeight: 400 }}> · optional</Text>
                        )}
                      </Text>
                      <Tag color={STATUS_COLOR[status.kind]} style={{ marginInlineEnd: 0, flexShrink: 0 }}>
                        {status.label}
                      </Tag>
                    </div>
                    <Paragraph type="secondary" style={{ margin: "4px 0 2px", fontSize: 12.5, lineHeight: 1.5 }}>
                      {item.description}
                    </Paragraph>
                    {item.caution && (
                      <Paragraph strong style={{ margin: "0 0 2px", fontSize: 12.5, lineHeight: 1.5 }}>
                        {item.caution}
                      </Paragraph>
                    )}
                    <Text style={{ fontSize: 12, color: status.kind === "missing" ? "#cf1322" : "rgba(0,0,0,0.55)" }}>
                      {status.detail}
                    </Text>
                    {status.warning && (
                      <Paragraph strong style={{ margin: "4px 0 0", fontSize: 12.5, lineHeight: 1.5, color: "#cf1322" }}>
                        {status.warning}
                      </Paragraph>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: 20 }}>
            {allRequiredChecked ? (
              <Alert type="success" showIcon message={<HashReveal expectedHash={expectedHash} storedHash={storedHash} />} />
            ) : (
              <Text type="secondary" style={{ fontSize: 13 }}>
                Attest every required item to reveal the contract hash. Disabled checkboxes are
                missing from the definition — implement the handler (or its explicit <Text code>No…</Text> opt-out) first.
              </Text>
            )}
          </div>
        </>
      )}
    </>
  );
}

function HashReveal({ expectedHash, storedHash }: { expectedHash: string; storedHash?: string }) {
  return (
    <div>
      <Paragraph style={{ marginBottom: 6 }}>
        Paste this into the widget's <Text code>contractHash</Text> field:
      </Paragraph>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <Text code style={{ fontSize: 14 }}>{expectedHash}</Text>
        <Button
          size="small"
          icon={<CopyOutlined />}
          onClick={() => {
            navigator.clipboard.writeText(expectedHash);
            message.success("Copied");
          }}
        >
          Copy
        </Button>
        {storedHash === expectedHash && <Tag color="green">matches current</Tag>}
      </div>
    </div>
  );
}

function ExperimentalFlagsSection() {
  const [flags, setFlags] = useState<ExperimentalFlags>(getExperimentalFlags);

  const toggle = (key: keyof ExperimentalFlags) => {
    const next = { ...flags, [key]: !flags[key] };
    setExperimentalFlags(next);
    setFlags(next);
  };

  const reset = () => {
    resetExperimentalFeatures();
    setFlags(getExperimentalFlags());
    message.success("Experimental features reset");
  };

  return (
    <>
      <Title level={5} style={{ marginTop: 0, marginBottom: 4 }}>
        Experimental Flags
      </Title>
      <Paragraph type="secondary" style={{ marginBottom: 16, fontSize: 13 }}>
        In-progress features, saved in this browser. Toggle one on to try it out — it may change
        or disappear as it bakes.
      </Paragraph>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {EXPERIMENTAL_FLAG_META.map((f) => (
          <div
            key={f.key}
            style={{
              display: "flex",
              gap: 12,
              alignItems: "flex-start",
              padding: "12px 14px",
              border: "1px solid #f0f0f0",
              borderRadius: 8,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <Text strong>{f.label}</Text>
              <Paragraph type="secondary" style={{ margin: "4px 0 0", fontSize: 12.5, lineHeight: 1.5 }}>
                {f.description}
              </Paragraph>
            </div>
            <Switch checked={flags[f.key]} onChange={() => toggle(f.key)} />
          </div>
        ))}
      </div>

      <Popconfirm
        title="Reset experimental features?"
        description="Turns every experimental feature off and clears the data it saved in this browser (flags and custom dashboard order)."
        okText="Reset"
        okButtonProps={{ danger: true }}
        cancelText="Cancel"
        onConfirm={reset}
      >
        <Button danger icon={<ReloadOutlined />} style={{ marginTop: 16 }}>
          Reset Experimental Features
        </Button>
      </Popconfirm>
    </>
  );
}

function slugify(s: string): string {
  return s.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

function TemplateManagerSection() {
  const { state } = useDashboard();
  const { metricCatalog, subjectNames } = useTemplateExportMaps();
  const [workflow, setWorkflow] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ warnings: string[]; piiViolations: string[] } | null>(null);

  const slug = slugify(workflow || state.name || "template");
  const widgetCount = Object.keys(state.widgets ?? {}).length;

  const handleExport = async () => {
    setBusy(true);
    setResult(null);
    try {
      const serialized = { ...serializeDashboardSpec(state), name: state.name };
      const { template, warnings, piiViolations } = tokenizeDashboard({
        serialized: serialized as Parameters<typeof tokenizeDashboard>[0]["serialized"],
        metricCatalog,
        subjectNames,
      });
      if (piiViolations.length) {
        setResult({ warnings, piiViolations });
        message.error("Export blocked — team-specific data remains (see below).");
        return;
      }
      const png = await captureFirstPagePng();
      downloadTemplateFiles(slug, template, png);
      setResult({ warnings, piiViolations: [] });
      message.success(`Downloaded ${slug}.json${png ? ` + ${slug}.png` : ""}.`);
    } catch (e) {
      message.error(`Export failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Title level={5} style={{ marginTop: 0, marginBottom: 4 }}>
        Export as Template
      </Title>
      <Paragraph type="secondary" style={{ marginBottom: 16, fontSize: 13 }}>
        Turn the current dashboard into a reusable, de-identified template. Real metrics, players,
        positions and drills are replaced with placeholders that resolve per team on import.
        Downloads two files — drop them into <Text code>Templates/&lt;workflow&gt;/</Text> as{" "}
        <Text code>template.json</Text> + <Text code>preview.png</Text> and add a catalog entry.
      </Paragraph>

      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <Input
          addonBefore="Workflow"
          placeholder="weekly-load"
          value={workflow}
          onChange={(e) => setWorkflow(e.target.value)}
        />
        <Button type="primary" loading={busy} disabled={widgetCount === 0} onClick={handleExport}>
          Export ({widgetCount} widgets)
        </Button>
      </div>
      <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 16 }}>
        Files: <Text code>{slug}.json</Text> and <Text code>{slug}.png</Text>.
      </Paragraph>

      {result?.piiViolations.length ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="Export blocked — team-specific data survived"
          description={<ul style={{ margin: 0, paddingLeft: 18 }}>{result.piiViolations.map((v, i) => <li key={i}>{v}</li>)}</ul>}
        />
      ) : null}
      {result?.warnings.length ? (
        <Alert
          type="warning"
          showIcon
          message="Review before publishing"
          description={<ul style={{ margin: 0, paddingLeft: 18 }}>{result.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>}
        />
      ) : null}
    </>
  );
}

